"use client";

import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Star,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  getPublicCompanyReviews,
  type PublicReview,
} from "@/lib/public-reviews";

type PublicCompanyReviewsProps = {
  slug: string;
  initialRatingAverage?: number | null;
  initialRatingCount?: number;
};

function formatRating(value: number) {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(value));
}

function RatingStars({
  rating,
}: {
  rating: number;
}) {
  return (
    <div
      className="flex items-center gap-1"
      aria-label={`${rating} de 5 estrelas`}
    >
      {Array.from({ length: 5 }).map(
        (_, index) => {
          const filled = index < rating;

          return (
            <Star
              key={index}
              size={17}
              className={
                filled
                  ? "fill-amber-400 text-amber-400"
                  : "text-slate-200"
              }
            />
          );
        },
      )}
    </div>
  );
}

export default function PublicCompanyReviews({
  slug,
  initialRatingAverage = null,
  initialRatingCount = 0,
}: PublicCompanyReviewsProps) {
  const [reviews, setReviews] =
    useState<PublicReview[]>([]);
  const [ratingAverage, setRatingAverage] =
    useState<number | null>(
      initialRatingAverage,
    );
  const [ratingCount, setRatingCount] =
    useState(initialRatingCount);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState(false);

  const reviewsTrackRef =
    useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    getPublicCompanyReviews(slug)
      .then((response) => {
        if (cancelled) {
          return;
        }

        setReviews(response.results);
        setRatingAverage(
          response.rating_average,
        );
        setRatingCount(response.rating_count);
        setError(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  function scrollReviews(
    direction: "left" | "right",
  ) {
    reviewsTrackRef.current?.scrollBy({
      left:
        direction === "left"
          ? -360
          : 360,
      behavior: "smooth",
    });
  }

  if (
    !loading &&
    !error &&
    ratingCount === 0
  ) {
    return null;
  }

  return (
    <section
      id="avaliacoes"
      className="border-t border-slate-200 bg-white"
    >
      <div className="mx-auto w-full max-w-[1380px] px-5 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-bold text-blue-600">
              Experiência dos clientes
            </p>

            <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
              Avaliações dos clientes
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {ratingCount > 0 &&
              ratingAverage != null && (
                <div className="flex items-center gap-3 rounded-2xl bg-amber-50 px-4 py-2.5">
                  <Star
                    size={20}
                    className="fill-amber-400 text-amber-400"
                  />

                  <div>
                    <p className="text-base font-black text-slate-900">
                      {formatRating(
                        ratingAverage,
                      )}
                    </p>

                    <p className="text-xs font-semibold text-slate-500">
                      {ratingCount}{" "}
                      {ratingCount === 1
                        ? "avaliação"
                        : "avaliações"}
                    </p>
                  </div>
                </div>
              )}

            {!loading &&
              !error &&
              reviews.length > 1 && (
                <div className="hidden items-center gap-2 sm:flex">
                  <button
                    type="button"
                    onClick={() =>
                      scrollReviews("left")
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                    aria-label="Avaliações anteriores"
                  >
                    <ChevronLeft size={19} />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      scrollReviews("right")
                    }
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                    aria-label="Próximas avaliações"
                  >
                    <ChevronRight size={19} />
                  </button>
                </div>
              )}
          </div>
        </div>

        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-sm font-medium text-slate-500">
            <LoaderCircle
              size={18}
              className="animate-spin"
            />
            Carregando avaliações...
          </div>
        ) : error ? (
          <p className="mt-6 text-sm text-slate-500">
            Não foi possível carregar as avaliações agora.
          </p>
        ) : (
          <div
            ref={reviewsTrackRef}
            className="public-reviews-scrollbar-hidden -mx-5 mt-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 [scrollbar-width:none] sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 [&::-webkit-scrollbar]:hidden"
          >
            {reviews.map((review) => (
              <article
                key={review.id}
                className="w-[82vw] max-w-[340px] shrink-0 snap-start rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:w-[320px]"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-bold text-slate-900">
                      {review.customer_display_name}
                    </p>

                    <div className="mt-1.5">
                      <RatingStars
                        rating={review.rating}
                      />
                    </div>
                  </div>

                  {review.is_verified && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700">
                      <CheckCircle2
                        size={13}
                      />
                      Compra verificada
                    </span>
                  )}
                </div>

                {review.comment && (
                  <p className="mt-3 whitespace-pre-line text-sm leading-5 text-slate-600">
                    {review.comment}
                  </p>
                )}

                <p className="mt-3 text-xs font-medium text-slate-400">
                  {formatDate(
                    review.created_at,
                  )}
                </p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
